import { useEffect, useRef, useState } from 'react';
import { useVisaiChat, type ChartSpec, type Turn } from '../lib/useVisaiChat';

/**
 * visai as a launcher, not a page.
 *
 * A pill bottom-right of every screen; click it and a chat opens in place. The
 * point of moving it off its own route is reach — the question worth asking
 * ("is that dip on line 3 still there?") occurs to a person while they are
 * looking at the dip, not after they have navigated away to a chat page.
 *
 * WHO SEES IT. Mounted only inside the staff shell (see App.tsx), so a portal
 * customer never gets it — the same boundary the server enforces on the route.
 * And it renders nothing at all until /api/chat/status says visai is
 * configured: a launcher for a feature that is switched off is just a button
 * that apologises when pressed.
 */

const SUGGESTIONS = [
  'How did line 3 do last week?',
  'Which line is performing worst this week?',
  'What was rejected most yesterday?',
];

export function VisaiWidget() {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const chat = useVisaiChat();
  const [draft, setDraft] = useState('');
  const bottom = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch('/api/chat/status', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { available: false }))
      .then((s: { available: boolean }) => setAvailable(s.available))
      .catch(() => setAvailable(false));
  }, []);

  useEffect(() => {
    if (open) {
      bottom.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [open, chat.turns, chat.activity]);

  // Esc closes, the way every other transient panel does.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Off, or still asking — either way, no launcher.
  if (available !== true) return null;

  const send = (q: string) => {
    setDraft('');
    void chat.ask(q);
  };

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-label="visai"
          style={{
            position: 'fixed',
            right: 20,
            bottom: 88,
            width: 'min(400px, calc(100vw - 32px))',
            height: 'min(560px, calc(100vh - 130px))',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          <div
            className="row"
            style={{
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div className="row" style={{ gap: 8 }}>
              <span className="visai-chip" aria-hidden>
                QC
              </span>
              <strong>visai</strong>
            </div>
            <div className="row" style={{ gap: 6 }}>
              {chat.turns.length > 0 && (
                <button
                  className="btn sm ghost"
                  onClick={chat.reset}
                  disabled={chat.streaming}
                  title="Start over"
                >
                  New
                </button>
              )}
              <button
                className="btn sm ghost"
                onClick={() => setOpen(false)}
                aria-label="Close visai"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {chat.turns.length === 0 ? (
              <div style={{ margin: 'auto 0' }}>
                <div className="muted" style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
                  Ask about the line — the inspections, the products, the models. visai reads the
                  same figures as the dashboard.
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {SUGGESTIONS.map((s) => (
                    <button key={s} className="btn sm" onClick={() => send(s)} style={{ textAlign: 'left' }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              chat.turns.map((t, i) => <Bubble key={i} turn={t} />)
            )}

            {chat.activity.length > 0 && (
              <div className="muted" style={{ fontSize: 12, display: 'grid', gap: 3 }}>
                {chat.activity.map((a, i) => (
                  <div key={i}>· {a}</div>
                ))}
              </div>
            )}
            {chat.error && <div style={{ color: 'var(--bad)', fontSize: 13 }}>{chat.error}</div>}
            <div ref={bottom} />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (draft.trim()) send(draft);
            }}
            className="row"
            style={{ gap: 8, padding: 12, borderTop: '1px solid var(--border)' }}
          >
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={chat.streaming}
              placeholder="How did line 3 do last week?"
              style={{ flex: 1 }}
            />
            <button className="btn primary sm" type="submit" disabled={chat.streaming || !draft.trim()}>
              {chat.streaming ? '…' : 'Ask'}
            </button>
          </form>
        </div>
      )}

      {/*
        The launcher wears the product's own mark, not a generic AI sparkle —
        the styling, including the turning gradient, is in index.css.
      */}
      <button
        className="visai-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close visai' : 'Ask visai'}
        title="Ask visai"
      >
        <span className="sheen" aria-hidden />
        <span className={open ? 'mark close' : 'mark'}>{open ? '✕' : 'TVQC'}</span>
      </button>
    </>
  );
}

function Bubble({ turn }: { turn: Turn }) {
  const mine = turn.role === 'user';
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
      <div
        style={{
          maxWidth: mine ? '85%' : '100%',
          width: mine ? undefined : '100%',
          background: mine ? 'var(--bg-panel-2)' : 'transparent',
          border: mine ? '1px solid var(--border)' : 'none',
          borderRadius: 12,
          padding: mine ? '8px 12px' : 0,
          fontSize: 14,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
        }}
      >
        {turn.content ? formatInline(turn.content) : !turn.charts?.length && <span className="muted">…</span>}
        {turn.charts?.map((c, i) => (
          <MiniChart key={i} spec={c} />
        ))}
      </div>
    </div>
  );
}

const STATUS = { good: '#0ca30c', unsure: '#fab219', bad: '#d03b3b' };

/**
 * A small chart drawn from a tool's figures, shown beside visai's words.
 *
 * Two forms only, because the tools produce two shapes: a stacked column per
 * time bucket (verdicts over time) and a horizontal bar per row (pass rate by
 * line or customer, or defect counts). Both use the reserved status colours
 * where the data is a verdict, and a single neutral accent where it is a plain
 * magnitude — so colour never claims a meaning the number does not have.
 */
function MiniChart({ spec }: { spec: ChartSpec }) {
  return (
    <div
      style={{
        marginTop: 10,
        padding: 10,
        background: 'var(--bg-panel-2)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        whiteSpace: 'normal',
      }}
    >
      <div className="muted" style={{ fontSize: 12, marginBottom: 8, fontWeight: 600 }}>
        {spec.title}
      </div>
      {spec.kind === 'timeseries' ? <MiniTimeseries spec={spec} /> : <MiniBars spec={spec} />}
    </div>
  );
}

function MiniTimeseries({ spec }: { spec: Extract<ChartSpec, { kind: 'timeseries' }> }) {
  const W = 320;
  const H = 96;
  const b = spec.buckets;
  const max = Math.max(1, ...b.map((x) => x.good + x.unsure + x.bad));
  const bandW = W / b.length;
  const barW = Math.max(1.5, Math.min(bandW - 2, 16));
  const fmt = (iso: string) =>
    spec.bucket === 'hour'
      ? new Date(iso).toLocaleTimeString([], { hour: '2-digit' })
      : new Date(iso).toLocaleDateString([], { day: '2-digit', month: '2-digit' });
  const every = Math.ceil(b.length / 6);
  return (
    <svg viewBox={`0 0 ${W} ${H + 16}`} width="100%" style={{ display: 'block' }}>
      {b.map((x, i) => {
        const cx = i * bandW + (bandW - barW) / 2;
        let yTop = H;
        const segs = [
          ['good', x.good, STATUS.good],
          ['unsure', x.unsure, STATUS.unsure],
          ['bad', x.bad, STATUS.bad],
        ] as const;
        return (
          <g key={i}>
            {segs
              .filter(([, v]) => v > 0)
              .map(([k, v, c], si) => {
                const h = (v / max) * H;
                yTop -= h;
                const gap = si > 0 ? 1.5 : 0;
                return <rect key={k} x={cx} y={yTop + gap} width={barW} height={Math.max(0, h - gap)} fill={c} rx={1.5} />;
              })}
            {i % every === 0 && (
              <text x={cx + barW / 2} y={H + 12} textAnchor="middle" fontSize={9} fill="var(--text-dim)">
                {fmt(x.t)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function MiniBars({ spec }: { spec: Extract<ChartSpec, { kind: 'bars' }> }) {
  const rows = spec.rows.slice(0, 8);
  const max = Math.max(1, ...rows.map((r) => r.value));
  // Pass rate is a status-ish magnitude; a bare count is neutral. One accent for
  // magnitude keeps the bars from implying a category they don't have.
  const fill = spec.unit === '%' ? 'var(--accent)' : '#6366f1';
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {rows.map((r) => (
        <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
            <strong style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
              {r.value}
              {spec.unit}
            </strong>
          </div>
          <div style={{ height: 8, background: 'var(--bg-panel)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${(r.value / max) * 100}%`, height: '100%', background: fill, borderRadius: 4 }} />
          </div>
          {r.sub && (
            <span className="muted" style={{ fontSize: 10 }}>
              {r.sub}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The one bit of Markdown the model actually uses: **bold** for the number that
 * was asked for. Rendered rather than shown literally, because "**97,6%**" on
 * screen reads as a typo, not emphasis. Deliberately not a Markdown library —
 * one inline rule does not need a parser or a dependency, and anything the model
 * emits is its own text, not third-party HTML, so there is nothing to sanitise.
 */
function formatInline(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}
